import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuthStore } from './authStore.js';
import { api, ApiError } from '../../lib/apiClient.js';
import { msg } from '../../lib/messages.js';
import './auth.css';

type Mode = 'login' | 'register' | 'reset';

export function AuthScreen({ mode }: { mode: Mode }): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resetToken = params.get('token');
  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const online = navigator.onLine;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        navigate('/innstillinger');
      } else if (mode === 'register') {
        await register(email, password);
        navigate('/innstillinger');
      } else if (resetToken) {
        await api.confirmReset(resetToken, password);
        setNotice(msg.auth.resetDone);
      } else {
        await api.requestReset(email);
        setNotice(msg.auth.resetSent);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : msg.auth.genericError);
    } finally {
      setBusy(false);
    }
  };

  const title =
    mode === 'login' ? msg.auth.login : mode === 'register' ? msg.auth.register : msg.auth.resetTitle;

  const goBack = (): void => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="auth">
      <button
        type="button"
        className="auth__back"
        aria-label={msg.common.back}
        onClick={goBack}
      >
        <ChevronLeft size={24} aria-hidden="true" />
      </button>

      <div className="auth__card">
        <div className="auth__brand numeral">{msg.app.name}</div>
        <p className="auth__tagline">{msg.auth.tagline}</p>

        {!online && <div className="auth__offline">{msg.auth.offline}</div>}

        <form className="auth__form" onSubmit={submit}>
        <h1 className="auth__title">{title}</h1>

        {/* Provider slot — empty in v1 (spec 06); Vipps Login is config, not surgery. */}

        {(mode !== 'reset' || !resetToken) && (
          <label className="auth__field">
            <span>{msg.auth.email}</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
        )}

        {(mode !== 'reset' || resetToken) && (
          <label className="auth__field">
            <span>{resetToken ? msg.auth.newPassword : msg.auth.password}</span>
            <input
              type="password"
              autoComplete={mode === 'register' || resetToken ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </label>
        )}

        {error && <div className="auth__error">{error}</div>}
        {notice && <div className="auth__notice">{notice}</div>}

        <button type="submit" className="btn btn--accent auth__submit" disabled={busy || !online}>
          {busy
            ? msg.auth.working
            : mode === 'login'
              ? msg.auth.login
              : mode === 'register'
                ? msg.auth.register
                : resetToken
                  ? msg.auth.resetConfirm
                  : msg.auth.resetSend}
        </button>

        <div className="auth__links">
          {mode === 'login' && (
            <>
              <Link to="/glemt-passord">{msg.auth.forgot}</Link>
              <Link to="/registrer">{msg.auth.toRegister}</Link>
            </>
          )}
          {mode === 'register' && <Link to="/logg-inn">{msg.auth.toLogin}</Link>}
          {mode === 'reset' && <Link to="/logg-inn">{msg.auth.toLogin}</Link>}
        </div>
      </form>

        <p className="auth__footer">
          {msg.auth.footer}{' '}
          <Link to="/">{msg.auth.continueAnon}</Link>
        </p>
      </div>
    </div>
  );
}
